import { useMessages } from '@features/l10n/l10nInjector.js';
import { Chord } from '@shared/components/Chord.js';
import { FmDropdownMenu } from '@shared/components/FmDropdownMenu.js';
import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import { MenuGutter } from '@shared/components/MenuGutter.js';
import { OnlineOnlyItem } from '@shared/components/OnlineOnlyItem.js';
import { useModalLink } from '@shared/components/ShowModalLink.js';
import { Toolbar } from '@shared/components/Toolbar.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { integratedLayerDefs } from '@shared/mapDefinitions.js';
import { type ReactElement, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  FaFileContract,
  FaInfoCircle,
  FaList,
  FaLock,
  FaMoneyBillWave,
  FaRegCopyright,
} from 'react-icons/fa';
import { useAttributionInfo } from './useAttributionInfo.js';

export function CopyrightButton(): ReactElement {
  const m = useMessages();

  const skCs = useAppSelector((state) =>
    ['sk', 'cs'].includes(state.l10n.language),
  );

  const customLayers = useAppSelector((state) => state.map.customLayers);

  const layers = useAppSelector((state) => state.map.layers);

  const legendLayers = useMemo(
    () =>
      new Set([
        ...(skCs ? ['A', 'T', 'C', 'K'] : []),
        'X',
        'O',
        ...[...integratedLayerDefs, ...customLayers]
          .filter((def) => def.technology === 'wms')
          .map((def) => def.type),
      ]),

    [customLayers, skCs],
  );

  const showLegendButton = layers.some((type) => legendLayers.has(type));

  const modalLink = useModalLink();

  const showAttribution = useAttributionInfo();

  return (
    <Toolbar className="me-2 mb-2">
      <Dropdown>
        <LongPressTooltip label={m?.general.infoAndLegal ?? '…'}>
          {({ props }) => (
            <Dropdown.Toggle
              bsPrefix="fm-dropdown-toggle-nocaret"
              id="dropdown-basic"
              variant="secondary"
              {...props}
            >
              <FaInfoCircle />
            </Dropdown.Toggle>
          )}
        </LongPressTooltip>

        <FmDropdownMenu style={{ width: 'max-content' }}>
          <Dropdown.Item
            key="attribution"
            as="button"
            onClick={() => showAttribution()}
          >
            <FaRegCopyright /> {m?.main.copyright}
          </Dropdown.Item>

          {showLegendButton && (
            <OnlineOnlyItem {...modalLink({ type: 'legend' })}>
              <FaList /> {m?.mainMenu.mapLegend}
              <MenuGutter>
                <Chord modal="legend" />
              </MenuGutter>
            </OnlineOnlyItem>
          )}

          <Dropdown.Item
            key="privacyPolicy"
            {...modalLink({ type: 'document', key: 'privacyPolicy' })}
          >
            <FaLock /> {m?.general.privacyPolicy}
          </Dropdown.Item>

          <Dropdown.Item
            key="termsOfService"
            {...modalLink({ type: 'document', key: 'termsOfService' })}
          >
            <FaFileContract /> {m?.general.termsOfService}
          </Dropdown.Item>

          {/* Terms of a purchase, and an embed sells nothing — its premium gem
              leaves for the portal, which states them where the money is
              actually taken. The two policies above stay: the embed does
              process the visitor's data, and they are using the service. */}
          {!window.fmEmbedded && (
            <Dropdown.Item
              key="refundPolicy"
              {...modalLink({ type: 'document', key: 'refundPolicy' })}
            >
              <FaMoneyBillWave /> {m?.general.refundPolicy}
            </Dropdown.Item>
          )}
        </FmDropdownMenu>
      </Dropdown>
    </Toolbar>
  );
}
